/**
 * Motion to Withdraw as Attorney of Record (Kelsi, 19 Sep 0:58:15).
 *
 * Kelsi asked for this twice: the only withdrawal on the case screen was the one for a surety bond,
 * which is rare, while withdrawing because a client has stopped paying or has fired the firm is
 * common and had no button at all.
 *
 * The grounds, their extra fields and the consent wording are fetched rather than written here.
 * Lloyd edits that library himself, so a copy in this file would be wrong the first time he changes
 * a sentence. See `WithdrawalMotionApi.options`.
 *
 * Blockers disable the button. Warnings never do: combining a confidential ground with a stated one
 * is his judgement to make, not an error to prevent.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WithdrawalMotionApi, {
  ConfirmedSettingDto,
  GroundDefinitionDto,
  SelectedGroundDto,
  WithdrawalBlockerDto,
  WithdrawalMotionBlockedError,
  WithdrawalMotionResultDto,
  WithdrawalOptionsDto,
} from '../../api/WithdrawalMotionApi';

function pdfObjectUrl(base64: string): string {
  const bytes = Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
  return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
}

function todayDateOnly(): string {
  return new Date().toISOString().slice(0, 10);
}

const WithdrawalMotionPanel: React.FC<{ Ticket_guid: string }> = ({ Ticket_guid }) => {
  const [options, setOptions] = useState<WithdrawalOptionsDto | null>(null);
  const [selected, setSelected] = useState<Record<string, Record<string, string>>>({});
  const [consent, setConsent] = useState<string>('');
  const [settings, setSettings] = useState<ConfirmedSettingDto[]>([]);
  const settingsSeeded = useRef(false);
  const [noSettingsKnown, setNoSettingsKnown] = useState(false);
  const [requestReset, setRequestReset] = useState(false);
  const [resetSettingDescription, setResetSettingDescription] = useState('');
  const [conferenceStatus, setConferenceStatus] = useState('');
  const [conferenceDate, setConferenceDate] = useState('');
  const [prosecutorName, setProsecutorName] = useState('');
  const [clientServiceMethod, setClientServiceMethod] = useState('email and first-class U.S. Mail');
  const [showClientApproval, setShowClientApproval] = useState(false);
  // Off by default. Generating is reading the wording; filing it on the case is a separate decision.
  const [fileOnCase, setFileOnCase] = useState(false);
  const [servedMethod, setServedMethod] = useState('electronic service');
  const [servedOn, setServedOn] = useState('the attorney for the State');
  const [servedAt, setServedAt] = useState(todayDateOnly());

  const [blockers, setBlockers] = useState<WithdrawalBlockerDto[]>([]);
  const [warnings, setWarnings] = useState<WithdrawalBlockerDto[]>([]);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<WithdrawalMotionResultDto | null>(null);
  const [motionUrl, setMotionUrl] = useState<string | null>(null);
  const [orderUrl, setOrderUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedGrounds: SelectedGroundDto[] = useMemo(
    () => Object.entries(selected).map(([id, fields]) => ({ id, fields })),
    [selected],
  );

  const substitution = useMemo(
    () =>
      (options?.grounds ?? []).some(
        (ground) => ground.is_substitution && Object.keys(selected).includes(ground.id),
      ),
    [options, selected],
  );

  useEffect(() => {
    let cancelled = false;
    WithdrawalMotionApi.options()
      .then((loaded) => {
        if (!cancelled) setOptions(loaded);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the grounds.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /*
   * Live preview of what is still missing.
   *
   * The catch is deliberately silent: generate re-checks on the server and returns the same
   * blockers, so a failed preview must not stop someone who knows the case is ready.
   */
  const loadReadiness = useCallback(async () => {
    try {
      const response = await WithdrawalMotionApi.readiness(Ticket_guid, {
        grounds: JSON.stringify(selectedGrounds),
        settings: JSON.stringify(settings),
        consent,
        noSettingsKnown: noSettingsKnown ? 'true' : undefined,
        requestReset: requestReset ? 'true' : undefined,
        resetSettingDescription,
        clientServiceMethod,
        servedAt,
        servedMethod,
        servedOn,
        signatureDate: todayDateOnly(),
      });
      setBlockers(response.readiness.blockers);
      setWarnings(response.readiness.warnings);
      // Seed the settings table once, from what the case already knows. The flag is what makes it
      // once: a case with no settings seeds an empty array, and testing the length instead would
      // hand `settings` a new array on every call, which this callback depends on.
      if (!settingsSeeded.current) {
        settingsSeeded.current = true;
        setSettings(
          response.settings.map((setting) => ({
            type: setting.suggestedType ?? '',
            date: setting.date,
            time: '',
          })),
        );
      }
      if (!prosecutorName && response.prosecutorName) setProsecutorName(response.prosecutorName);
    } catch {
      // See above.
    }
  }, [
    Ticket_guid,
    selectedGrounds,
    settings,
    consent,
    noSettingsKnown,
    requestReset,
    resetSettingDescription,
    clientServiceMethod,
    servedAt,
    servedMethod,
    servedOn,
    prosecutorName,
  ]);

  useEffect(() => {
    loadReadiness();
  }, [loadReadiness]);

  useEffect(
    () => () => {
      if (motionUrl) URL.revokeObjectURL(motionUrl);
      if (orderUrl) URL.revokeObjectURL(orderUrl);
    },
    [motionUrl, orderUrl],
  );

  const toggleGround = (ground: GroundDefinitionDto): void => {
    setSelected((current) => {
      const next = { ...current };
      if (next[ground.id]) {
        delete next[ground.id];
        return next;
      }
      next[ground.id] = Object.fromEntries(
        ground.fields.map((field) => [field.id, field.default ?? '']),
      );
      // Lloyd pairs each ground with the consent answer it usually implies.
      if (Object.keys(current).length === 0) setConsent(ground.suggest_consent);
      return next;
    });
  };

  const setGroundField = (groundId: string, fieldId: string, value: string): void => {
    setSelected((current) => ({
      ...current,
      [groundId]: { ...current[groundId], [fieldId]: value },
    }));
  };

  const handleGenerate = async (): Promise<void> => {
    setGenerating(true);
    setError(null);
    setResult(null);
    try {
      const generated = await WithdrawalMotionApi.generate(Ticket_guid, {
        grounds: selectedGrounds,
        consent: consent || null,
        // Rows are sent as they stand, minus the entirely empty ones. Filtering out a half-filled
        // row here is what let a real setting disappear from a motion that lists them all; the
        // server blocks on it instead.
        settings: settings.filter((setting) => setting.type || setting.date || setting.time),
        noSettingsKnown,
        requestReset,
        resetSettingDescription: resetSettingDescription || null,
        conference: conferenceStatus
          ? { status: conferenceStatus, date: conferenceDate, prosecutorName }
          : null,
        clientServiceMethod,
        showClientApproval,
        fileOnCase,
        servedAt,
        servedMethod,
        servedOn,
      });
      setResult(generated);
      setWarnings(generated.warnings);
      setMotionUrl(pdfObjectUrl(generated.motion.pdfBase64));
      setOrderUrl(pdfObjectUrl(generated.order.pdfBase64));
      await loadReadiness();
    } catch (err) {
      if (err instanceof WithdrawalMotionBlockedError) {
        setBlockers(err.blockers.length > 0 ? err.blockers : [{ field: null, reason: err.message }]);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to generate the motion.');
      }
    } finally {
      setGenerating(false);
    }
  };

  if (!options) {
    return (
      <>
        <Typography variant="h6" gutterBottom>
          Motion to Withdraw as Attorney of Record
        </Typography>
        <CircularProgress size={20} />
        <Divider sx={{ my: 3 }} />
      </>
    );
  }

  return (
    <>
      <Typography variant="h6" gutterBottom>
        Motion to Withdraw as Attorney of Record
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generates the motion and the proposed order together. Nothing is filed from here.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {blockers.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {blockers.map((blocker) => (
              <li key={`${blocker.field}-${blocker.reason}`}>{blocker.reason}</li>
            ))}
          </ul>
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {warnings.map((warning) => (
              <li key={`${warning.field}-${warning.reason}`}>{warning.reason}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Why counsel is withdrawing
      </Typography>
      <Stack spacing={0.5} sx={{ mb: 2 }}>
        {options.grounds.map((ground) => (
          <Box key={ground.id}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(selected[ground.id])}
                  onChange={() => toggleGround(ground)}
                />
              }
              label={<Typography variant="body2">{ground.label}</Typography>}
            />
            {ground.usage_note && Boolean(selected[ground.id]) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                {ground.usage_note}
              </Typography>
            )}
            {Boolean(selected[ground.id]) && ground.fields.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ ml: 4, mt: 1, mb: 1, flexWrap: 'wrap' }}>
                {ground.fields.map((field) => (
                  <TextField
                    key={field.id}
                    size="small"
                    label={field.label}
                    required={field.required}
                    type={field.type === 'date' ? 'date' : field.type === 'number' ? 'number' : 'text'}
                    InputLabelProps={field.type === 'date' ? { shrink: true } : undefined}
                    value={selected[ground.id]?.[field.id] ?? ''}
                    onChange={(event) => setGroundField(ground.id, field.id, event.target.value)}
                    sx={{ minWidth: 220, mb: 1 }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>

      {/* A substitution says nothing about consent: the client is choosing the new attorney. */}
      {!substitution && (
        <TextField
          select
          size="small"
          fullWidth
          label="Does the client agree?"
          value={consent}
          onChange={(event) => setConsent(event.target.value)}
          sx={{ mb: 2 }}
        >
          {options.consent_options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Pending settings
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Listed in the motion under Rule 10. Confirm what kind of setting each one is, since the
        calendar records only a title.
      </Typography>
      <Stack spacing={1} sx={{ mb: 1 }}>
        {settings.map((setting, index) => (
          <Stack key={`${setting.date}-${index}`} direction="row" spacing={1} alignItems="center">
            <TextField
              select
              size="small"
              label="Type"
              value={setting.type}
              onChange={(event) =>
                setSettings((current) =>
                  current.map((row, at) => (at === index ? { ...row, type: event.target.value } : row)),
                )
              }
              sx={{ minWidth: 220 }}
            >
              {options.setting_types.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={setting.date}
              onChange={(event) =>
                setSettings((current) =>
                  current.map((row, at) => (at === index ? { ...row, date: event.target.value } : row)),
                )
              }
            />
            <TextField
              size="small"
              label="Time"
              placeholder="9:00 a.m."
              value={setting.time ?? ''}
              onChange={(event) =>
                setSettings((current) =>
                  current.map((row, at) => (at === index ? { ...row, time: event.target.value } : row)),
                )
              }
              sx={{ width: 140 }}
            />
            <IconButton
              size="small"
              onClick={() => setSettings((current) => current.filter((_, at) => at !== index))}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button
          size="small"
          onClick={() => setSettings((current) => [...current, { type: '', date: '', time: '' }])}
        >
          Add a setting
        </Button>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={noSettingsKnown}
              onChange={(event) => setNoSettingsKnown(event.target.checked)}
            />
          }
          label={<Typography variant="body2">There are none</Typography>}
        />
      </Stack>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={requestReset}
            onChange={(event) => setRequestReset(event.target.checked)}
          />
        }
        label={<Typography variant="body2">Ask the court to reset a setting</Typography>}
      />
      {requestReset && (
        <TextField
          size="small"
          fullWidth
          label="Which setting"
          placeholder="Pretrial Hearing on October 20, 2026"
          value={resetSettingDescription}
          onChange={(event) => setResetSettingDescription(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
      )}

      <Paper variant="outlined" sx={{ p: 2, my: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Service
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Served on"
            value={servedOn}
            onChange={(event) => setServedOn(event.target.value)}
            sx={{ minWidth: 240, mb: 1 }}
          />
          <TextField
            size="small"
            label="Served by"
            value={servedMethod}
            onChange={(event) => setServedMethod(event.target.value)}
            sx={{ minWidth: 200, mb: 1 }}
          />
          <TextField
            size="small"
            type="date"
            label="Served on date"
            InputLabelProps={{ shrink: true }}
            value={servedAt}
            onChange={(event) => setServedAt(event.target.value)}
            sx={{ mb: 1 }}
          />
        </Stack>
        <TextField
          size="small"
          fullWidth
          label="How the client was given a copy"
          helperText="The motion states that a copy was delivered, so it has to have been."
          value={clientServiceMethod}
          onChange={(event) => setClientServiceMethod(event.target.value)}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Certificate of conference
          <Chip label="optional" size="small" variant="outlined" sx={{ ml: 1, height: 18 }} />
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="The State's position"
            value={conferenceStatus}
            onChange={(event) => setConferenceStatus(event.target.value)}
            sx={{ minWidth: 240, mb: 1 }}
          >
            <MenuItem value="">
              <em>No conference</em>
            </MenuItem>
            {options.conference_options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          {conferenceStatus && (
            <>
              <TextField
                size="small"
                label="Prosecutor"
                value={prosecutorName}
                onChange={(event) => setProsecutorName(event.target.value)}
                sx={{ minWidth: 220, mb: 1 }}
              />
              <TextField
                size="small"
                type="date"
                label="Conferred on"
                InputLabelProps={{ shrink: true }}
                value={conferenceDate}
                onChange={(event) => setConferenceDate(event.target.value)}
                sx={{ mb: 1 }}
              />
            </>
          )}
        </Stack>
      </Paper>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={showClientApproval}
            onChange={(event) => setShowClientApproval(event.target.checked)}
          />
        }
        label={<Typography variant="body2">Include a signature line for the client</Typography>}
      />

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={fileOnCase}
            onChange={(event) => setFileOnCase(event.target.checked)}
          />
        }
        label={
          <Typography variant="body2">
            Also save a copy on the case, visible to staff only
          </Typography>
        }
      />

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          disabled={generating || blockers.length > 0}
          onClick={handleGenerate}
          startIcon={generating ? <CircularProgress size={18} /> : undefined}
        >
          {generating ? 'Generating…' : 'Generate motion and order'}
        </Button>
      </Box>

      {result && motionUrl && orderUrl && (
        <Alert severity="success" sx={{ mt: 2 }}>
          <Stack spacing={0.5}>
            <MuiLink href={motionUrl} target="_blank" rel="noopener">
              Open {result.motion.fileName}
            </MuiLink>
            <MuiLink href={orderUrl} target="_blank" rel="noopener">
              Open {result.order.fileName}
            </MuiLink>
            <Typography variant="caption" color="text.secondary">
              {result.filedOnCase
                ? 'Both are saved on this case under Documents, visible to staff only.'
                : 'Nothing was saved on the case. Tick the box above before generating if you want a copy kept.'}
            </Typography>
          </Stack>
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />
    </>
  );
};

export default WithdrawalMotionPanel;
